import React, { Component, Fragment } from "react";
import { Redirect, Link } from "react-router-dom";
import Markdown from "react-markdown";
import RemarkGFM from "remark-gfm";
import ContentLoader from "react-content-loader";
import { toast } from "react-toastify";
import MetaTags from "react-meta-tags";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

// Assets
import IconInfo from "./../assets/imgs/icons/info-circle.svg";

// Layout Components
import * as Layout from "./../components/layout/";

// Page Parts
import ProductConfig from "./../components/product/product-config";
import ProductDetailsCard from "./../components/product/details-card";
import ProductConfigLoading from "./../components/product/product-config-loading";
import OtherProducts from "../components/product/other-products";

import RedirectModal from "./../components/common/modal/redirect";
import ConfirmModal from "./../components/common/modal/confirm";
import DeDuplicationModal from "../components/common/modal/deduplication";
import ProfileUpdateModal from "../components/profile/modals/update";
import PopUpImage from "../components/common/modal/popup-image";

import UBXButton from "./../components/ubx/button";
import UBXNotice from "./../components/ubx/notice";
import Logo from "./../components/common/logo";
import Footer from "./../components/common/footer";

import User from "./../components/user/user";

import EKYCModal from "./../components/ekyc/modal";

// Config
import { ProductMeta } from "./../config/meta";
import { ValidProfileCheckFields } from "./../config/loan-application";
import { ProductRedirect, ToggleOtherProducts } from "./../config/products";
import { EKYC } from "./../config/feature-flags";

// API functions
import { borrowerAddWishlist, borrowerGetWishlist, borrowerDeleteWishlist } from "./../api/borrower";
import { getProductById, getProductCardsConfig, getProducts, getRequirementsURL } from "./../api/products";
import { preQualifyRestrictions } from "../api/pre-qualification";
import { getEKYCStatus } from "./../api/ekyc";

// Helpers
import { getReferer, getPathParams, getQueryString, getLocationPath } from "./../helpers/props";
import { getStaticText, getPagePath, getENV } from "./../helpers/system";
import { isUUID, generateQueryString } from "./../helpers/string";
import { properNumber } from "./../helpers/number";
import { getLenderLogoCached } from "./../helpers/lenders";
import { featureEnabled } from "./../helpers/feature-flags";

import ProductText from "./../const/product.json";
import ExternalText from "./../const/external.json";
import Provinces from "./../const/provinces.json";
import Cities from "./../const/cities.json";
import AllZipCodes from "./../const/bgy-zip-code/all.json";

import "./../assets/css/containers/products.css";
import ProductDetails from "../components/product/product-details";
import DraftDeDuplicationModal from "../components/common/modal/draft-deduplication";

const productParamsMap = {
    amount: "loanAmount",
    term: "loanTerm",
};

const InvestreeTopOrg = "IN002";

export default class Product extends Component {
    _mounted = false;

    constructor(props) {
        super(props);
        this.state = this.getInitialState();
    }

    getInitialState() {
        return {
            ready: false,
            redirect: false,
            error: false,
            busy: false,
            product: {},
            productReady: false,
            lender: {},
            lenderReady: false,
            loanAmortization: null,
            loanTerm: null,
            loanAmount: null,
            darkMode: true,
            scrollLimit: "100%",
            wishlistAdded: false,
            userLoggedIn: false,
            wishlist: [],
            wishlistReady: false,
            showEKYC: false,
            showLogin: false,
            showModal: false,
            showDedupModal: false,
            showDraftDedupModal: false,
            showProfileUpdateModal: false,
            dedupLoading: false,
            prequalRestrictions: {},
            prequalError: false,
            requirementsURL: {},
            requirementsURLReady: false,
            showProductRequirements: false,
        };
    }

    async addToWishlist(payload) {
        let success = false;

        try {
            const response = await borrowerAddWishlist(payload);

            const { status, data } = response;

            if (status === 200) {
                success = true;

                toast.success("Wishlist Added!");
                this.saveWishlist(data);
            }
        } catch (e) {
            this.handleError(e, "addToWishlist");
        }

        return success;
    }

    checkProductPath(urlPath) {
        const { product_id: productId } = getPathParams(this.props);

        if (isUUID(productId)) {
            if (urlPath) {
                const redirect = getPagePath("Product", { "product_id": urlPath });

                this.setState({ redirect });

                return false;
            }
        }
    }

    handleLenderCode(salesOrgs, topOrg) {
        const lenderCode = [topOrg];
        if (salesOrgs.length > 0) {
            salesOrgs.forEach((sOrg) => {
                if (sOrg.code !== topOrg) {
                    lenderCode.push(sOrg.code);
                }
            });
        }

        return lenderCode;
    }

    async getProductDetails() {
        const { product_id: productId } = getPathParams(this.props);
        let { loanAmount, loanTerm, lenderLogos } = this.state;

        if (this._mounted) await this.setState({ busy: true });

        try {
            const productResponse = await getProductById(productId);
            const { long_descriptions: longDescriptions = {} } = await this.getProductCardsConfig();

            const { url_path: urlPath = false } = productResponse;

            this.checkProductPath(urlPath);

            if (productResponse) {
                const {
                    id,
                    name,
                    code,
                    version,
                    categories,
                    locations,
                    rate = {},
                    min_amt: minAmount,
                    max_amt: maxAmount,
                    processing_fee: processingFee,
                    approval_time: approvalTime,
                    loan_terms: loanTerms,
                    terms: requirements,
                    top_org: topOrg,
                    sales_orgs: salesOrgs,
                    repay_type: repayType,
                    e_commerce: eCommerce = false,
                    latest_draft_id: latestDraftId,
                    config = {},
                } = productResponse;

                const { initial_loan_amount: initialLoanAmount } = config;

                const { interestRate, annualPercentageRate } = rate["rateConfig"];

                const lenderCode = this.handleLenderCode(salesOrgs, topOrg);

                let salesOrg = null;
                lenderCode.forEach((code) => {
                    salesOrg = code !== topOrg && code;
                });

                const lenderLogo = lenderLogos[lenderCode.join("-")];

                const productOrg = lenderCode.join("_");

                const longDescription = longDescriptions[`${productOrg}-${code}`] || longDescriptions[productOrg];

                const product = {
                    id,
                    name,
                    categories,
                    approvalTime,
                    processingFee,
                    minAmount,
                    maxAmount,
                    loanTerms,
                    requirements,
                    interestRate,
                    annualPercentageRate,
                    topOrg,
                    salesOrg,
                    repayType,
                    locations,
                    product: {
                        code,
                        version,
                    },
                    productOrg,
                    eCommerce,
                    config,
                    lenderLogo,
                    longDescription,
                    latestDraftId,
                };

                loanAmount = loanAmount || initialLoanAmount || minAmount;
                loanTerm = loanTerm || Math.min(...loanTerms);

                if (this._mounted) {
                    await this.setState({
                        loanAmount,
                        loanTerm,
                        product,
                        productReady: true,
                    });
                }

                await this.calculateAmortization();

                this.onResize();
            }
        } catch (e) {
            this.handleError(e, "getProductDetails");
        }

        if (this._mounted) this.setState({ busy: false });

        return true;
    }

    async getLenderDetails() {
        const loadedLenders = await getLenderLogoCached();

        if (loadedLenders) {
            const lenderLogos = {};

            Object.keys(loadedLenders).forEach((topOrg) => {
                const { logo, name, ...salesOrgs } = loadedLenders[topOrg];

                if (!lenderLogos[topOrg]) {
                    if (logo) {
                        lenderLogos[topOrg] = { logo, name };
                    }
                }

                Object.keys(salesOrgs).forEach((salesOrg) => {
                    const { logo, name } = loadedLenders[topOrg][salesOrg];

                    lenderLogos[`${topOrg}-${salesOrg}`] = { logo, name };
                });
            });

            if (this._mounted) {
                await this.setState({ lenderLogos, lenderReady: true });
            }
        }
    }

    async getProductCardsConfig() {
        let result = false;
        try {
            result = await getProductCardsConfig();
        } catch (e) {
            this.handleError(e, "getProductCardsConfig");
        }

        return result;
    }

    async getProductsList() {
        const { product_id: filterProductId } = getPathParams(this.props);

        let productList = false;
        try {
            const { product = {}, lenderLogos = {} } = this.state;
            const { minAmount } = product;

            const productsResponse = await getProducts({ amount: minAmount });
            const { short_descriptions: shortDescriptions } = await this.getProductCardsConfig();

            if (productsResponse) {
                const { results } = productsResponse;
                const products = [];

                await results.map((result) => {
                    const {
                        id,
                        name,
                        min_amount: minAmount,
                        max_amount: maxAmount,
                        loan_term: loanTerm,
                        interest_rate: interestRate,
                        sales_org: salesOrgs,
                        product,
                        terms_and_conditions: requirements,
                        repay_type: repayType,
                        categories,
                        top_org: topOrg,
                        url_path: urlPath = false,
                        config,
                    } = result;

                    if (id !== filterProductId) {
                        const lenderCode = [topOrg];
                        if (salesOrgs.length > 1) {
                            salesOrgs.forEach((item) => {
                                if (item.code !== topOrg) {
                                    lenderCode.push(item.code);
                                }
                            });
                        }

                        const lenderLogo = lenderLogos[lenderCode.join("-")];

                        const productOrg = lenderCode.join("_");
                        const shortDescriptionKey = `${productOrg}-${product.code}`;
                        const shortDescription = shortDescriptions[shortDescriptionKey];

                        products.push({
                            id,
                            name,
                            minAmount,
                            maxAmount,
                            loanTerm,
                            interestRate,
                            salesOrg: salesOrgs,
                            product,
                            requirements,
                            repayType,
                            categories,
                            topOrg,
                            lenderLogo,
                            urlPath,
                            shortDescription,
                            config,
                        });
                    }

                    return false;
                });

                productList = products;
            }
        } catch (e) {
            this.onHandleError(e);
        }

        return productList;
    }

    async getEKYCStatus() {
        let userEKYCStatus = false;

        try {
            const ekycStatusResponse = await getEKYCStatus();

            const { "ekyc_status": ekycStatusRaw = false } = ekycStatusResponse;

            // update ekyc status in profile
            const profile = User.getProfile();

            profile["ekycStatus"] = ekycStatusRaw;

            User.setProfile(profile);

            userEKYCStatus = ekycStatusRaw;

            this.setState({ userEKYCStatus });
        } catch (e) {
            //
        }
    }

    async componentDidMount() {
        this._mounted = true;

        this.setState({ referrerPage: getReferer(this.props) });

        await this.checkParams();
        await this.checkUser();
        if (featureEnabled(EKYC)) {
            await this.getEKYCStatus();
        }
        await this.checkUserProfile();

        await this.saveUTMDetails();

        if (this.state.userLoggedIn) {
            await this.initializeWishlist();
        }

        await this.getRequirementsURL();
        await this.getLenderDetails();

        await this.getProductDetails();

        this.initializeEvents();

        if (this._mounted) {
            this.setState({ ready: true });
        }
    }

    async componentDidUpdate(prevProps) {
        const { product_id: oldProductId } = getPathParams(prevProps);
        const { product_id: newProductId } = getPathParams(this.props);

        if (oldProductId !== newProductId) {
            window.location.reload();
        }
    }

    componentWillUnmount() {
        this._mounted = false;

        this.unInitializeEvents();
    }

    initializeEvents() {
        window.addEventListener("popstate", (e) => {
            e.preventDefault();
            const { referrerPage } = this.state;

            if (referrerPage) {
                window.history.replaceState("", "", referrerPage);
                window.location.reload();
            }
        });

        window.addEventListener("resize", (e) => this.onResize());
    }

    unInitializeEvents() {
        window.removeEventListener("resize", (e) => this.onResize());
    }

    onResize() {
        const container = document.querySelector("#product-container-left");

        if (container) {
            const rect = container.getBoundingClientRect();

            if (this._mounted) {
                this.setState({ scrollLimit: rect.height });
            }
        }
    }

    async initializeWishlist() {
        const { userLoggedIn } = this.state;

        const wishlist = JSON.parse(sessionStorage.getItem("wishlist"));

        if (userLoggedIn) {
            if (!wishlist) {
                await this.getWishlist();
            } else {
                if (this._mounted) {
                    this.setState({ wishlist, wishlistReady: true });
                }
            }

            this.handleChangeWishlist();
        }
    }

    async getRequirementsURL() {
        try {
            const requirementsURL = await getRequirementsURL();

            await this.setState({ requirementsURL, requirementsURLReady: true });
        } catch (e) {
            this.handleError(e, "getRequirementsURL");
        }
    }

    async checkParams() {
        const queryParams = getQueryString(this.props);

        const initState = {};
        for (let queryParam in queryParams) {
            const stateParam = productParamsMap[queryParam];

            initState[stateParam] = queryParams[queryParam];
        }

        if (this._mounted) {
            await this.setState(initState);
        }
    }

    async checkUser() {
        const userLoggedIn = User.get() ? true : false;

        if (this._mounted) {
            await this.setState({ userLoggedIn });
        }
    }

    async checkUserProfile() {
        const userProfile = User.getProfile() || false;

        let userHasValidProfile = false;
        let userEKYCStatus = false;
        if (userProfile) {
            const checkedFields = ValidProfileCheckFields.map((field) => {
                let invalidField = field;
                if (userProfile[field] !== null && userProfile[field] !== undefined && userProfile[field] !== "") {
                    invalidField = false;
                }

                return invalidField;
            }).filter((item) => item || false);

            userHasValidProfile = checkedFields.length ? checkedFields : true;
            userEKYCStatus = userProfile["ekycStatus"];
        }

        if (this._mounted) {
            await this.setState({ userHasValidProfile, userEKYCStatus });
        }
    }

    async saveUTMDetails() {
        const { search } = this.props.location;

        if (this._mounted) {
            await User.saveCampaignData(search);
        }
    }

    async onHandleError(err) {
        let error = false;
        const { response, message: errorMessage } = err;

        console.log(err, "ERTT");

        if (response) {
            const { data } = response;
            const { status, message: responseMessage = "Something went wrong", data: responseData } = data;

            switch (status) {
                case 400: {
                    const { message, error: errorItems } = responseData;

                    let errorContent = "";
                    let errorContentItems = [];
                    for (let errorCode in errorItems) {
                        errorContentItems.push(<li key={errorCode}>{errorItems[errorCode]}</li>);
                    }

                    if (errorContentItems.length > 0) {
                        errorContent = <ul>{errorContentItems}</ul>;
                    }

                    error = (
                        <Fragment>
                            <h3>{message}</h3>
                            {errorContent}
                        </Fragment>
                    );
                    break;
                }
                case 404: {
                    this.setState({ redirect: getPagePath("NotFound") });
                    break;
                }
                default: {
                    error = responseMessage;
                    break;
                }
            }
        } else {
            if (errorMessage === "Network Error") {
                error = "Connection error. Please try again later.";
            }
        }

        if (this._mounted) this.setState({ error });
    }

    handleError(e, source) {
        if (e.response) {
            const {
                response: {
                    data: { status },
                },
            } = e;

            if (!["getRequirementsURL", "getWishlist"].includes(source)) {
                switch (status) {
                    case 404: {
                        if (source === "getProductDetails") {
                            toast.error("Product your looking for does not exist", { autoClose: 10000 });
                            this.setState({
                                redirect: getPagePath("Products"),
                            });
                        }
                        break;
                    }
                    case "FAILED": {
                        toast.error("You are offline. Please check your connection.");
                        break;
                    }
                    default:
                        toast.error("Something went wrong.");
                        break;
                }
            }
        }
    }

    handleShowEKYC() {
        const { userEKYCStatus } = this.state;

        let checkPrequal = true;

        if (featureEnabled(EKYC)) {
            if ((userEKYCStatus === null || userEKYCStatus) && userEKYCStatus?.status !== "verified") {
                checkPrequal = false;
                this.setState({ showEKYC: true });
            }
        }

        return checkPrequal;
    }

    onProductAction(e) {
        e.preventDefault();
        e.stopPropagation();

        const {
            userLoggedIn,
            userHasValidProfile,
            product: { productOrg },
        } = this.state;

        if (ProductRedirect[productOrg]) {
            const { link = false } = ProductRedirect[productOrg];

            if (link) {
                this.redirectToExternal(link);
            }
        } else {
            if (userLoggedIn) {
                const checkPrequal = this.handleShowEKYC();

                if (checkPrequal) {
                    if (userHasValidProfile === true) {
                        this.checkPrequalRestrictions();
                    } else {
                        this.setState({ showProfileUpdateModal: true });
                    }
                }
            } else {
                this.setState({ showLogin: true });
            }
        }
    }

    handleLoginSuccess() {
        window.location.reload();
    }

    handleLoginClose() {
        this.setState({ showLogin: false });
    }

    async handleChangeAmount(loanAmount) {
        if (this._mounted) {
            await this.setState({ loanAmount });
        }

        this.calculateAmortization();
        this.handleChangeWishlist();
    }

    async handleChangeTerm(loanTerm) {
        if (this._mounted) {
            await this.setState({ loanTerm });
        }

        this.calculateAmortization();
        this.handleChangeWishlist();
    }

    async handleChangeWishlist() {
        const { wishlist, loanTerm, loanAmount, product } = this.state;

        const wishlistAdded = wishlist.some(
            (item) =>
                item.productId === product.id &&
                item.loanTerm === Number(loanTerm) &&
                item.loanAmount === Number(loanAmount),
        );

        if (this._mounted) {
            this.setState({ wishlistAdded });
        }
    }

    async handleAddToWishlist() {
        const { product: productData = {}, loanAmount, loanTerm, wishlistAdded, busy } = this.state;

        const { topOrg, product = {} } = productData;

        if (!wishlistAdded && !busy) {
            await this.setState({ busy: true });

            const payload = {
                "product_code": product.code,
                "top_org": topOrg,
                "loan_amount": loanAmount,
                "tenor": loanTerm,
            };

            let result = await this.addToWishlist(payload);

            await this.setState({
                wishlistAdded: result,
                busy: false,
            });
        }
    }

    closeWishlistModal() {
        this.setState({ showModal: false });
    }

    openWishlistModal(e) {
        e.preventDefault();

        this.setState({ showModal: true });
    }

    async handleDeleteWishlist() {
        const { wishlist, product, loanTerm, loanAmount } = this.state;

        try {
            const wishlistItem = wishlist.find(
                (item) =>
                    item.productId === product.id &&
                    item.loanTerm === Number(loanTerm) &&
                    item.loanAmount === Number(loanAmount),
            );

            const wishlistResponse = await borrowerDeleteWishlist(wishlistItem.id);

            const savedWishlist = JSON.parse(sessionStorage.getItem("wishlist"));

            if (savedWishlist) {
                const newWishlist = savedWishlist.filter((wishlist) => {
                    return wishlist.id !== wishlistItem.id;
                });
                sessionStorage.setItem("wishlist", JSON.stringify(newWishlist));

                this.setState({
                    wishlist: newWishlist,
                    wishlistAdded: false,
                });
            }

            const { data } = wishlistResponse;
            const { message } = data;

            toast.success(message);
        } catch (e) {
            this.handleError(e, "handleDeleteWishlist");
        }
    }

    async getWishlist() {
        try {
            const wishlistResponse = await borrowerGetWishlist();
            if (wishlistResponse) {
                const { data } = wishlistResponse;

                this.saveWishlist(data);
            }
        } catch (e) {
            this.handleError(e, "getWishlist");
        }
    }

    saveWishlist(wishlistItem) {
        let wishlist;

        if (wishlistItem instanceof Array) {
            wishlist = wishlistItem.map((wishlist) => {
                const { id, loan_amount: loanAmount, loan_term: loanTerm, product_details: productDetails } = wishlist;
                const { id: productId, name } = productDetails;

                return {
                    id,
                    productId,
                    loanAmount,
                    loanTerm,
                    name,
                };
            });
        } else {
            const { id, loan_amount: loanAmount, loan_term: loanTerm, product_details: productDetails } = wishlistItem;
            const { id: productId, name } = productDetails;
            wishlist = {
                id,
                productId,
                loanAmount,
                loanTerm,
                name,
            };
        }

        const newWishlist = this.state.wishlist.concat(wishlist);
        this.setState({
            wishlist: newWishlist,
            wishlistReady: true,
        });

        sessionStorage.setItem("wishlist", JSON.stringify(this.state.wishlist));
    }

    async calculateAmortization() {
        const { loanAmount, loanTerm, product } = this.state;
        const { interestRate } = product;

        if (loanAmount && loanTerm) {
            const queryString = `?${new URLSearchParams({
                amount: loanAmount,
                term: loanTerm,
            }).toString()}`;

            window.history.pushState("", "", queryString);

            if (this._mounted) {
                await this.setState({
                    loanAmortization: loanAmount / loanTerm + loanAmount * (interestRate / 100),
                    wishlistAdded: false,
                });
            }
        }
    }

    async checkPrequalRestrictions() {
        const { product } = this.state;
        const { product: productConfig, topOrg } = product;
        const { code } = productConfig;

        const payload = {
            "product_code": code,
            "top_org": topOrg,
        };

        await this.setState({ dedupLoading: true, showDedupModal: true, dedupAction: "loan-application" });
        const prequalRestrictions = await preQualifyRestrictions(payload);

        const { loan_restricted: loanRestricted = {}, ...prequalRestricts } = prequalRestrictions;
        const { declined, active, pending, draft } = loanRestricted;

        let prequalError = [];
        const prequalCode = [];
        if (declined) {
            prequalError.push("loan_declined");
            prequalCode.push("1");
        } else {
            prequalCode.push("0");
            if (active || pending) {
                prequalError.push("loan_restricted");
                prequalCode.push("1");
            } else {
                prequalCode.push("0");
            }

            Object.keys(prequalRestricts).forEach((restrict) => {
                if (prequalRestricts[restrict] === true) {
                    prequalError.push(restrict);
                    prequalCode.push("1");
                } else {
                    prequalCode.push("0");
                }
            });
        }

        if (prequalError.length) {
            this.setState({ showDedupModal: true });
        } else if (draft) {
            this.setState({ showDedupModal: false, showDraftDedupModal: true });
        } else {
            this.setState({ showDedupModal: false });
            this.redirectToApplication(prequalCode.join(""));
        }

        this.setState({ prequalError, dedupLoading: false });
    }

    closeDedupModal() {
        this.setState({ showDedupModal: false });
        this.redirectToDashboard();
    }

    closeDraftDedupModal() {
        this.setState({ showDraftDedupModal: false });
    }

    closeProfileUpdateModal() {
        this.setState({ showProfileUpdateModal: false });
    }

    closeEKYCModal() {
        this.setState({ showEKYC: false });
    }

    closePopUpImage() {
        this.setState({ showProductRequirements: false });
    }

    redirectToExternal(to) {
        const params = generateQueryString({ to });

        this.setState({ redirect: `${getPagePath("Redirect")}${params}` });
    }

    redirectToApplication(prequalCode) {
        const { product_id: productId } = getPathParams(this.props);
        const { loanTerm: term, loanAmount: amount } = this.state;

        if (term && amount) {
            this.setState({
                redirect: getPagePath("LoanApplication", {
                    "product_id": productId,
                    term,
                    amount,
                    state: { prequalCode },
                }),
            });
        }

        this.closeDraftDedupModal();
    }

    redirectToExistingApplication() {
        const { product } = this.state;
        const { latestDraftId } = product;

        latestDraftId && this.setState({ redirect: getPagePath(["LoanApplication", 1], { "loan_id": latestDraftId }) });

        this.closeDraftDedupModal();
    }

    redirectToDashboard() {
        this.setState({ redirect: `${getPagePath("Dashboard")}` });
    }

    redirectModal() {
        const { product, productReady } = this.state;

        if (productReady) {
            const { productOrg, topOrg, salesOrg, config = {} } = product;
            const isCreditCard = config.loan_type === "credit_card";

            const productRedirect = isCreditCard ? ProductRedirect[topOrg][salesOrg] : ProductRedirect[productOrg];

            if (productRedirect) {
                const { link = false } = productRedirect;

                if (link) {
                    this.redirectToExternal(link);
                }
            }
        }
    }

    redirect() {
        const { redirect } = this.state;

        return redirect ? <Redirect push to={redirect} /> : false;
    }

    renderProductDetails(card) {
        const {
            product = {},
            loanAmortization,
            loanAmount,
            loanTerm,
            productReady,
            lenderReady,
            wishlistAdded,
            wishlistReady,
            userLoggedIn,
        } = this.state;

        return (
            loanAmortization &&
            (card ? (
                <ProductDetailsCard
                    product={product}
                    loanAmortization={loanAmortization}
                    loanTerm={loanTerm}
                    loanAmount={loanAmount}
                    productReady={productReady}
                    lenderReady={lenderReady}
                    wishlistReady={wishlistReady}
                    wishlistAdded={wishlistAdded}
                    userLoggedIn={userLoggedIn}
                    onProductAction={(e) => this.onProductAction(e)}
                    handleAddToWishlist={(e) => this.handleAddToWishlist(e)}
                    openWishlistModal={(e) => this.openWishlistModal(e)}
                />
            ) : (
                <ProductDetails
                    product={product}
                    loanTerm={loanTerm}
                    loanAmount={loanAmount}
                    productReady={productReady}
                />
            ))
        );
    }

    renderModal() {
        const {
            showModal,
            showDedupModal,
            showDraftDedupModal,
            showProfileUpdateModal,
            showProductRequirements,
            userHasValidProfile,
            dedupLoading,
            prequalError,
            productReady,
            requirementsURL,
            product,
        } = this.state;
        const { productOrg } = product;

        let confirmModal;
        let dedupModal;
        let draftDedupModal;
        let profileUpdateModal;
        let redirectModal;
        let popUpImage;

        let reqURL = requirementsURL[productOrg] || requirementsURL["others"];

        if (showModal) {
            confirmModal = (
                <ConfirmModal
                    id="deleteWishlist"
                    show={showModal}
                    agreeText={`Continue`}
                    declineText={`Cancel`}
                    onAgree={(e) => this.handleDeleteWishlist(e)}
                    onDecline={(e) => this.closeWishlistModal(e)}
                    onClose={(e) => this.closeWishlistModal(e)}
                >
                    <p>Remove from saved loan products?</p>
                </ConfirmModal>
            );
        }

        if (showProfileUpdateModal) {
            profileUpdateModal = (
                <ProfileUpdateModal
                    show={showProfileUpdateModal}
                    fields={userHasValidProfile}
                    onSuccess={(e) => this.checkPrequalRestrictions(e)}
                    onClose={(e) => this.closeProfileUpdateModal()}
                />
            );
        }

        if (showDedupModal) {
            dedupModal = (
                <DeDuplicationModal
                    show={showDedupModal}
                    action="loan-application"
                    loading={dedupLoading}
                    dedupError={prequalError}
                    // onLogin={(e) => this.onOpenLogin(e)}
                    redirectToDashboard={(e) => this.redirectToDashboard(e)}
                    onClose={(e) => this.closeDedupModal()}
                />
            );
        }

        if (showDraftDedupModal) {
            draftDedupModal = (
                <DraftDeDuplicationModal
                    show={showDraftDedupModal}
                    productName={product.name}
                    onClose={() => this.closeDraftDedupModal()}
                    onExistingDraftClick={() => this.redirectToExistingApplication()}
                    onNewApplicationClick={() => this.redirectToApplication(true)}
                />
            );
        }

        if (showProductRequirements) {
            popUpImage = (
                <PopUpImage
                    show={showProductRequirements}
                    onClose={() => this.closePopUpImage()}
                    documentsURL={reqURL}
                    title="Product Requirements"
                />
            );
        }

        if (productReady) {
            const { productOrg, topOrg, salesOrg, config = {} } = product;
            const isCreditCard = config.loan_type === "credit_card";

            const productRedirect = isCreditCard ? ProductRedirect[topOrg][salesOrg] : ProductRedirect[productOrg];

            if (productRedirect) {
                const { modal = false } = productRedirect;

                if (modal) {
                    const { after, message, header } = modal;

                    redirectModal = (
                        <RedirectModal
                            className="redirect"
                            key={`${productOrg}-${after}`}
                            show={true}
                            onRedirect={(e) => this.redirectModal(e)}
                            after={after}
                            header={header}
                            message={message}
                        />
                    );
                }
            }
        }

        return (
            <Fragment>
                {confirmModal}
                {profileUpdateModal}
                {dedupModal}
                {draftDedupModal}
                {redirectModal}
                {popUpImage}
            </Fragment>
        );
    }

    renderProductDetailCard() {
        const { product, productReady, loanAmortization, loanAmount, loanTerm, scrollLimit, userLoggedIn } = this.state;
        const { config = {}, productOrg } = product;
        const { loan_type: loanType } = config;
        let customClass = loanType ? loanType.replace(/_/g, "-") : "";

        let buttonText = "Apply";
        let buttonEnabled = loanTerm && loanAmount ? true : false;
        if (ProductRedirect[productOrg]) {
            const { text } = ProductRedirect[productOrg];
            buttonText = text;
        }

        return (
            <section id="product-card-container" style={{ height: scrollLimit }}>
                {userLoggedIn && this.renderHeader()}
                <div className="container-xl d-none d-md-block">
                    <div className="row">
                        <div className="col-lg-8 col-md-7"></div>
                        <div className="col-lg-4 col-md-5">
                            <div className={`product-card-side ${productReady ? "sticky" : ""}`}>
                                {this.renderProductDetails(true)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={`product-card-bottom d-flex flex-column d-md-none`}>
                    <div className="preview">
                        {productOrg !== InvestreeTopOrg ? (
                            <div className="amortization d-flex flex-column justify-content-center">
                                <p className="label">Estimated Monthly Amortization</p>
                                <p className="value">
                                    ₱ {loanAmortization ? properNumber(loanAmortization, 2) : "------"}/month
                                </p>
                            </div>
                        ) : (
                            <div className="amortization d-flex flex-column justify-content-center">
                                <p className="label">Financing Amount</p>
                                <p className="value">₱ {loanAmortization ? properNumber(loanAmount, 2) : "------"}</p>
                            </div>
                        )}
                        <UBXButton
                            type="primary"
                            className={customClass}
                            block={true}
                            disabled={!buttonEnabled}
                            onClick={(e) => this.onProductAction(e)}
                        >
                            {buttonText}
                        </UBXButton>
                    </div>
                </div>
                {this.renderModal()}
            </section>
        );
    }

    renderProductConfigBanner() {
        const { product = {}, productReady, darkMode = false, loanAmount, loanTerm } = this.state;
        const { config = {} } = product;
        const { loan_type: loanType } = config;

        let darkClassName = darkMode ? "bg-green" : "";
        return (
            <section id="product-banner" className={darkClassName}>
                <div className="container-xl">
                    <div className="row">
                        <div className="col-lg-8 col-md-7">
                            {productReady ? (
                                <ProductConfig
                                    productOrg={product["productOrg"]}
                                    minAmount={product["minAmount"]}
                                    maxAmount={product["maxAmount"]}
                                    termOptions={product["loanTerms"]}
                                    onChangeAmount={(loanAmount) => this.handleChangeAmount(loanAmount)}
                                    onChangeTerm={(loanTerm) => this.handleChangeTerm(loanTerm)}
                                    loanAmountValue={loanAmount}
                                    loanTermValue={loanTerm + ""}
                                    dark={darkMode}
                                    data={{ loanAmount, loanTerm }}
                                    loanType={loanType}
                                />
                            ) : (
                                <ProductConfigLoading />
                            )}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    checkLocationProvince(provinces = []) {
        return provinces.filter(
            (province) => province !== "_all_" && Provinces.find(({ value }) => value === province)?.value,
        );
    }

    checkLocationCity(cities = []) {
        let result = {};
        cities
            .filter((item) => Cities.find(({ value, province }) => item.city === value && item.province === province))
            .forEach((item) => {
                const province = item.province;
                const city = item.city;
                if (result.hasOwnProperty(province)) {
                    result[province].push(city);
                } else {
                    result[province] = [city];
                }
            });
        return result;
    }

    checkLocationZipCode(zipCodes = []) {
        let result = {};
        zipCodes
            .filter((item) =>
                AllZipCodes.find(({ zip, province }) => item.zip_code === zip.toString() && item.province === province),
            )
            .forEach((item) => {
                const province = item.province;
                const zipCode = item.zip_code;
                if (result.hasOwnProperty(province)) {
                    result[province].push(zipCode);
                } else {
                    result[province] = [zipCode];
                }
            });
        return result;
    }

    renderLocationNotice() {
        const { product } = this.state;
        const { productOrg, locations, config } = product;
        const { restriction_location_external: locationExternal } = config;

        if (locationExternal) {
            const { label, link } = locationExternal;
            return (
                <UBXNotice
                    key="location-notice-external"
                    className="location-notice external"
                    type="info"
                    show={true}
                    icon={true}
                >
                    <a href={link} target="_blank" rel="noopener noreferrer">
                        Click here to see if you're covered by {label}'s serviceable areas.
                    </a>
                </UBXNotice>
            );
        }

        if (locations instanceof Object) {
            const { province, city, zip_code: zipCode } = locations;

            const provinces = this.checkLocationProvince(province);
            const cities = this.checkLocationCity(city);
            const zipCodes = this.checkLocationZipCode(zipCode);

            const locationsNotice = [];

            provinces.forEach((provinceCode) => {
                const provinceName = Provinces.find(({ value }) => value === provinceCode).name;
                locationsNotice.push(
                    <li key={`${productOrg}_${provinceCode}`}>
                        <strong>{provinceName}</strong>
                    </li>,
                );
            });

            Object.keys(cities).forEach((provinceCode) => {
                const provinceName = Provinces.find(({ value }) => provinceCode === value).name;
                const cityNames = cities[provinceCode].map(
                    (cityCode) =>
                        Cities.find(({ value, province }) => cityCode === value && provinceCode === province)?.name,
                );

                locationsNotice.push(
                    <li key={`${productOrg}_${provinceCode}_cities`}>
                        <strong>{provinceName}</strong> (limited to {cityNames.join(", ")} only)
                    </li>,
                );
            });

            Object.keys(zipCodes).forEach((provinceCode) => {
                const provinceName = Provinces.find(({ value }) => provinceCode === value).name;
                const zipCodeNames = zipCodes[provinceCode].map(
                    (zipCode) =>
                        AllZipCodes.find(({ zip, province }) => zipCode === zip.toString() && provinceCode === province)
                            ?.zip,
                );

                locationsNotice.push(
                    <li key={`${productOrg}_${provinceCode}_zipcodes`}>
                        <strong>{provinceName}</strong> (limited to zip code: {zipCodeNames.join(", ")})
                    </li>,
                );
            });

            return (
                <UBXNotice
                    key="provinces-notice"
                    className="location-notice"
                    type="info"
                    show={true}
                    icon={true}
                    onClick={({ target }) => {
                        target.classList.toggle("mini");
                    }}
                >
                    Limited to businesses in the following locations:
                    <ul>{locationsNotice}</ul>
                </UBXNotice>
            );
        }
    }

    renderProductNotice() {
        const { product } = this.state;
        const { productOrg } = product;

        const noticeHeader = getStaticText(ExternalText, ["ExternalNoticeHeader", productOrg.toUpperCase()]) || false;
        const noticeContent = getStaticText(ExternalText, ["ExternalNoticeContent", productOrg.toUpperCase()]) || false;
        const noticeClassName = getStaticText(
            ExternalText,
            ["ExternalNoticeClassName", productOrg.toUpperCase()],
            {},
            true,
        );

        let renderedProductNotice = [];
        if (noticeHeader || noticeContent) {
            const productNoticeHeader = noticeHeader ? <h3>{noticeHeader}</h3> : "";
            const productNoticeContent = noticeContent ? <p>{noticeContent}</p> : "";

            renderedProductNotice.push(
                <UBXNotice className={noticeClassName} type="info" show={true} icon={true}>
                    {productNoticeHeader}
                    {productNoticeContent}
                </UBXNotice>,
            );
        }

        return renderedProductNotice;
    }

    renderDocumentGuideButton() {
        const { product, requirementsURL, requirementsURLReady } = this.state;
        const { productOrg, config } = product;
        const { loan_type: loanType } = config;

        const customClass = ["guide-button"];

        if (loanType) {
            customClass.push(loanType.replace(/_/g, "-"));
        }

        let reqURL = requirementsURL[productOrg];

        let renderedDocumentGuideButton = "";

        if (requirementsURLReady && reqURL) {
            renderedDocumentGuideButton = (
                <UBXButton
                    className={customClass.join(" ")}
                    onClick={() => this.setState({ showProductRequirements: true })}
                >
                    {getStaticText(ProductText, "labelDocumentGuide")}
                </UBXButton>
            );
        } else {
            reqURL = requirementsURL["others"];

            renderedDocumentGuideButton = (
                <a className={customClass.join(" ")} href={reqURL} target="_black" rel="noopener noreferrer">
                    {getStaticText(ProductText, "labelDocumentGuide")}
                </a>
            );
        }

        return renderedDocumentGuideButton;
    }

    renderHeader() {
        return (
            <section id="product-header">
                <div className="container-xl">
                    <div className="row">
                        <div className="col-6">
                            <Logo darkmode={true} />
                        </div>
                        <div className="col-6 text-right">
                            <span className="back-link">
                                <Link to={getPagePath("Dashboard")}>Back to dashboard</Link>
                            </span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    renderProductDescription() {
        const { product, productReady } = this.state;
        const { longDescription = "" } = product;

        return (
            longDescription && (
                <section id="product-description">
                    <div className="container-xl">
                        <div className="row">
                            <div className="col-lg-8 col-md-7">
                                {productReady ? (
                                    <Fragment>
                                        <h4 className="text-green-1">About</h4>
                                        <Markdown plugins={[RemarkGFM]} className="description-container">
                                            {longDescription}
                                        </Markdown>
                                    </Fragment>
                                ) : (
                                    <div className="loading">
                                        <ContentLoader width="100%">
                                            <rect width="30%" height="20" x="0" y="0" />
                                            <rect width="100%" height="20" x="0" y="70" />
                                        </ContentLoader>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )
        );
    }

    renderProductRequirements() {
        const { product, productReady } = this.state;
        const { longDescription = "" } = product;

        let markdownOptions = {
            renderers: {
                link: ({ href, children }) => {
                    let outputLink = "";
                    if (/^#tooltip:/.test(href)) {
                        let [, tooltipText = false] = /^#tooltip:(.+)/.exec(href);

                        if (tooltipText) {
                            tooltipText = decodeURI(tooltipText);
                            outputLink = (
                                <Fragment>
                                    {children}
                                    <OverlayTrigger overlay={<Tooltip placement="auto">{tooltipText}</Tooltip>}>
                                        <span>
                                            <img
                                                src={IconInfo}
                                                alt="Hover me for details"
                                                style={{ width: "16px", marginBottom: "2px", marginLeft: "2px" }}
                                            />
                                        </span>
                                    </OverlayTrigger>
                                </Fragment>
                            );
                        }
                    } else if (/^#tooltipList:/.test(href)) {
                        const [, tooltipList = false] = /^#tooltipList:(.+)/.exec(decodeURI(href));

                        if (tooltipList) {
                            const tooltipListItems = tooltipList.split("|").map((item) => {
                                let listItem = "";
                                if (/\[(.+)\]/.test(item)) {
                                    let [, subList = false] = /\[(.+)\]/.exec(item);

                                    if (subList) {
                                        const subListItems = subList
                                            .split(",")
                                            .map((subListItem) => <li key={subListItem}>{subListItem}</li>);

                                        const subListText = [];
                                        item.split(`[${subList}]`).forEach((itemElement) => {
                                            if (itemElement) {
                                                subListText.push(itemElement);
                                                subListText.push(<ul>{subListItems}</ul>);
                                            }
                                        });

                                        listItem = <li>{subListText}</li>;
                                    }
                                } else {
                                    listItem = <li>{item}</li>;
                                }

                                return listItem;
                            });

                            const tooltipListText = <ul>{tooltipListItems}</ul>;

                            outputLink = (
                                <Fragment>
                                    {children}
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip placement="auto" className="tooltip-list">
                                                {tooltipListText}
                                            </Tooltip>
                                        }
                                    >
                                        <span>
                                            <img
                                                src={IconInfo}
                                                alt="Hover me for details"
                                                style={{ width: "16px", marginBottom: "2px", marginLeft: "2px" }}
                                            />
                                        </span>
                                    </OverlayTrigger>
                                </Fragment>
                            );
                        }
                    }

                    if (outputLink === "") {
                        outputLink = <a href={href}>{children}</a>;
                    }

                    return outputLink;
                },
            },
            disallowedTypes: ["paragraph"],
            unwrapDisallowed: true,
        };

        return (
            <section id="product-requirements" className={longDescription ? "narrow" : ""}>
                <div className="container-xl">
                    <div className="row">
                        <div className="col-lg-8 col-md-7">
                            {productReady ? (
                                <Fragment>
                                    {this.renderProductNotice()}
                                    {this.renderLocationNotice()}
                                    <h4 className="text-green-1">Requirements</h4>
                                    <Markdown
                                        plugins={[RemarkGFM]}
                                        className="requirements-container"
                                        {...markdownOptions}
                                    >
                                        {product["requirements"]}
                                    </Markdown>
                                    {this.renderDocumentGuideButton()}
                                </Fragment>
                            ) : (
                                <div className="loading">
                                    <ContentLoader width="100%">
                                        <rect width="30%" height="20" x="0" y="0" />
                                        <rect width="100%" height="20" x="0" y="40" />
                                        <rect width="100%" height="20" x="0" y="70" />
                                        <rect width="100%" height="20" x="0" y="100" />
                                        <rect width="70%" height="20" x="0" y="130" />
                                    </ContentLoader>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    renderOtherProducts() {
        const { product = {}, productReady } = this.state;
        const { productOrg, topOrg, product: productCode = {} } = product;
        const { code } = productCode;

        let renderedOtherProducts = "";
        if (productReady && !ToggleOtherProducts[getENV()].includes(`${topOrg}_${code}`)) {
            renderedOtherProducts = (
                <OtherProducts
                    viewingProductOrg={productOrg}
                    viewingTopOrg={topOrg}
                    viewingProductCode={code}
                    getProducts={() => this.getProductsList()}
                />
            );
        }

        return renderedOtherProducts;
    }

    render() {
        const path = getLocationPath(this.props);
        const { product_id: productId } = getPathParams(this.props);
        const {
            showEKYC,
            showLogin,
            userLoggedIn,
            userEKYCStatus,
            product: { name: productName, lenderLogo: lenderLogoData = {} },
        } = this.state;

        const lenderName = lenderLogoData?.name;
        const lenderLogo = lenderLogoData?.logo;

        const {
            title: productMetaTitle = `${productName} by ${lenderName} | SeekCap`,
            description: productMetaDescription = `Apply for ${productName} by ${lenderName} on SeekCap`,
        } = ProductMeta[productId] || {};

        let ekycModal = "";
        if (featureEnabled(EKYC)) {
            ekycModal = (
                <EKYCModal
                    status={userEKYCStatus}
                    show={showEKYC}
                    onRedirect={(url) => window.open(url)}
                    onClose={(e) => this.closeEKYCModal(e)}
                />
            );
        }

        return (
            this.redirect() || (
                <Fragment>
                    {userLoggedIn ? (
                        <div id="product-details-user">
                            {this.renderProductDetailCard()}
                            <div id="product-container-left">
                                {this.renderProductConfigBanner()}
                                {this.renderProductDetails()}
                                {this.renderProductDescription()}
                                {this.renderProductRequirements()}
                            </div>
                            {this.renderOtherProducts()}
                            <Footer />
                        </div>
                    ) : (
                        <Layout.Default
                            id="product-details"
                            showLogin={showLogin}
                            onLoginSuccess={(e) => this.handleLoginSuccess(e)}
                            onLoginClose={(e) => this.handleLoginClose(e)}
                        >
                            {this.renderProductDetailCard()}
                            <div id="product-container-left">
                                {this.renderProductConfigBanner()}
                                {this.renderProductDetails()}
                                {this.renderProductDescription()}
                                {this.renderProductRequirements()}
                            </div>
                            {this.renderOtherProducts()}
                        </Layout.Default>
                    )}
                    {ekycModal}
                    <MetaTags>
                        <title>{productMetaTitle}</title>
                        <meta name="title" content={productMetaTitle} />
                        <meta property="og:title" content={productMetaTitle} />
                        <meta name="description" content={productMetaDescription} />
                        <meta property="og:description" content={productMetaDescription} />
                        <meta property="og:image" content={lenderLogo} />
                        <meta property="og:url" content={`https://www.seekcap.ph${path}`} />
                    </MetaTags>
                </Fragment>
            )
        );
    }
}
